// sla.view — tab ⏱️ SLA & Waktu: SEMUA batas waktu aplikasi dalam satu tempat.
// Dipisah dari settings.view karena isinya 25+ setelan; dikelompokkan supaya tak jadi
// satu halaman panjang yang melelahkan dibaca.
import { esc } from '@domain/format';
import { kebijakan, absensi, saveSetting, getSetting, type Kebijakan } from '@core/settings';
import { DEFAULT_AMBANG, type AmbangSehat } from '@domain/finansial';
import { toast } from '@ui/toast';

const dis = (b: boolean): string => (b ? '' : 'disabled');

// Status ber-SLA yang bisa diatur menitnya (Siap/Sudah Diambil tak ber-SLA). Port SOP §5.
const SLA_STATUSES: [string, string][] = [
  ['Diterima', '📥 Diterima → ditugaskan'],
  ['Diagnosa', '🔍 Diagnosa selesai'],
  ['Menunggu Persetujuan', '⏳ Menunggu jawaban pelanggan'],
  ['Dikerjakan', '🔧 Dikerjakan selesai'],
];

/** Satu kelompok setelan = satu panel lipat. Dibuka-tutup pakai <details> bawaan browser
 *  supaya daftar 25+ setelan tak jadi satu halaman panjang. Kelompok pertama terbuka. */
const grup = (judul: string, isi: string, catatan = '', buka = false): string =>
  `<details class="setgrp" ${buka ? 'open' : ''}><summary>${esc(judul)}</summary>` +
  (catatan ? `<div class="scope-note">${catatan}</div>` : '') + `<div class="row2">${isi}</div></details>`;

export function slaTab(body: HTMLElement, canEdit: boolean): void {
  const k = kebijakan();
  const sh: AmbangSehat = { ...DEFAULT_AMBANG, ...getSetting('sehat_ambang', {}) };
  const ab = absensi();   // ambang telat/alpha ikut di sini: semua "berapa lama" dalam satu daftar
  const numF = (id: string, lbl: string, val: number, hint = ''): string =>
    `<div class="field"><label>${esc(lbl)}</label><input id="${id}" type="number" min="0" value="${val}" ${dis(canEdit)}>${hint ? `<div style="font-size:11px;color:var(--muted);margin-top:2px">${esc(hint)}</div>` : ''}</div>`;
  const slaInputs = SLA_STATUSES.map(([key, lbl]) =>
    `<div class="field"><label>${esc(lbl)} (menit)</label><input class="k_sla" data-status="${esc(key)}" type="number" min="0" value="${k.slaCfg[key] ?? 0}" ${dis(canEdit)}></div>`).join('');
  body.innerHTML = `<div class="panel" style="max-width:720px">
    <div class="scope-note">Semua batas waktu aplikasi ada di sini. Terlewati → badge ⏰ + pengingat, dan <b>dipakai KPI/Target</b>. Isi <b>0</b> untuk menonaktifkan sebuah batas. Klik judul untuk buka/tutup.</div>

    ${grup('⏰ SLA Operasional (SOP §5)',
      `<div class="field" style="grid-column:1/-1"><label><input type="checkbox" id="k_slaon" ${k.slaOn ? 'checked' : ''} ${dis(canEdit)}> Aktifkan timer SLA operasional</label></div>${slaInputs}`,
      'Berapa lama sebuah tiket boleh berada di tiap status sebelum dianggap terlambat.', true)}

    ${grup('💰 SLA Finance', `
      ${numF('k_slafbayar', 'Verifikasi pembayaran (menit)', k.slaVerifikasiBayarMenit, 'sejak CS tandai LUNAS → Finance verifikasi')}
      ${numF('k_slafrefund', 'Verifikasi refund (menit)', k.slaVerifikasiRefundMenit, 'sejak CS ajukan refund → Finance verifikasi')}
      ${numF('k_slaapproval', 'Approval hapus/void §26 (menit)', k.slaApprovalMenit, 'sejak SPV ajukan → Owner/Manager putuskan')}
      ${numF('k_slapenagih', 'Eskalasi penagihan piutang (hari)', k.slaPenagihanEskalasiHari, 'hari LEWAT jatuh tempo sebelum eskalasi')}`,
      'Batas waktu proses keuangan sejak tugas berpindah ke Finance.')}

    ${grup('📦 Unit Belum Diambil & Biaya Simpan', `
      ${numF('k_ambang', 'Ambang "belum diambil" (hari)', k.ambangBelum)}
      ${numF('k_simpanmulai', 'Biaya simpan mulai (hari)', k.simpanMulaiHari, 'fee mulai setelah N hari')}
      ${numF('k_simpanlepas', 'Lepas tanggung jawab (hari)', k.simpanLepasHari, '>N hari fee = 0')}
      ${numF('k_simpaninterval', 'Interval tambah bulan (hari)', k.simpanIntervalHari, '+1 bulan tiap N hari')}`)}

    ${grup('📒 Piutang & Aging', `
      ${numF('k_tempohari', 'Jatuh tempo default (hari)', k.tempoHari, 'terisi saat kasir pilih "Piutang / Tempo"; masih bisa diubah per nota')}
      ${numF('k_jedaingat', 'Jeda hubungi ulang (hari)', k.jedaIngatHari, '0 = tanpa jeda; mencegah pelanggan sama ditagih berkali-kali')}
      ${numF('k_agb1', 'Aging bucket 1 s/d (hari)', k.agingB1Hari)}
      ${numF('k_agb2', 'Aging bucket 2 s/d (hari)', k.agingB2Hari)}
      ${numF('k_agb3', 'Aging bucket 3 s/d (hari)', k.agingB3Hari)}`,
      'Bucket umur di Laporan Piutang: 0–B1 · (B1+1)–B2 · (B2+1)–B3 · &gt;B3.')}

    ${grup('🔄 Alur Servis & Pengingat', `
      ${numF('k_grace', 'Kelonggaran lewat estimasi (hari)', k.graceEstimasiHari, 'disebut ⏰ terlambat setelah N hari melewati estimasi selesai')}
      ${numF('k_stale', 'Menunggu jawaban pelanggan (hari)', k.staleKonfirmasiHari, 'setelah N hari tanpa jawaban → CS diingatkan')}`,
      'Kapan tiket dianggap <b>terlambat</b> atau <b>terlalu lama menunggu</b> — dipakai ⚠️ Perlu Diperhatikan, pengingat harian, dan KPI kecepatan.')}

    ${grup('🕒 Absensi', `
      ${numF('k_telatsetengah', 'Telat → ½ hari setelah (menit)', ab.telatSetengah, 'lewat toleransi shift + N menit')}
      ${numF('k_telatalpha', 'Telat → Alpha setelah (menit)', ab.telatAlpha, 'dianggap tidak masuk')}
      ${numF('k_reminderabsen', 'Pengingat sebelum jam masuk (menit)', ab.reminderMenit, '0 = tanpa pengingat')}`,
      'Ambang ini menentukan kategori kehadiran, dan kategori itulah yang <b>langsung dipakai KPI kehadiran</b>. Toleransi per shift diatur di tab 🕒 Pengaturan Absensi.')}

    ${grup('🩺 Ambang Kesehatan Keuangan', `
      ${numF('s_kasbahaya', 'Ketahanan kas — bahaya di bawah (bulan)', sh.kasBulanBahaya)}
      ${numF('s_kasperhatian', 'Ketahanan kas — perhatian di bawah (bulan)', sh.kasBulanPerhatian)}
      ${numF('s_macet', 'Piutang macet maksimal (% pendapatan)', sh.macetPersen)}
      ${numF('s_konsentrasi', 'Satu pelanggan maksimal (% pendapatan)', sh.konsentrasiPersen)}
      ${numF('s_kualitas', 'Biaya kualitas maksimal (% pendapatan)', sh.kualitasPersen, 'servis ulang + klaim garansi')}
      ${numF('s_turun', 'Margin turun berturut-turut (bulan)', sh.turunBerturut)}`,
      'Dipakai analisa otomatis di menu 💰 Keuangan untuk memberi tanda <b>baik / perhatian / bahaya</b>.')}

    ${grup('🗑️ Masa Simpan Data & Tautan Dokumen', `
      ${numF('k_selfieret', 'Selfie absensi disimpan (hari)', k.selfieRetensiHari, '0 = selamanya; rekap kehadiran tetap utuh')}
      ${numF('k_draftexp', 'Isian belum selesai disimpan (hari)', k.draftKedaluwarsaHari, 'draft Terima Servis di perangkat')}
      ${numF('k_doklink', 'Masa berlaku tautan dokumen (hari)', k.dokLinkHari, 'nota/surat kuasa yang dikirim ke pelanggan lewat WA')}`,
      'Jejak audit sengaja <b>tidak</b> ada di sini: minimal 90 hari, dikunci basis data supaya jejak kecurangan tak bisa dihapus lebih awal.')}

    ${canEdit ? `<button class="btn primary" id="k_slasave" style="margin-top:14px">💾 Simpan SLA & Waktu</button>` : ''}</div>`;
  body.querySelector('#k_slasave')?.addEventListener('click', async () => {
    const num = (id: string, def: number): number => { const v = +(body.querySelector('#' + id) as HTMLInputElement).value; return isNaN(v) || v < 0 ? def : v; };
    const slaCfg: Record<string, number> = { 'Siap Diambil': 0 };
    body.querySelectorAll<HTMLInputElement>('.k_sla').forEach((el) => { slaCfg[el.dataset.status ?? ''] = Math.max(0, +el.value || 0); });
    const next: Kebijakan = {
      ...kebijakan(),
      slaOn: (body.querySelector('#k_slaon') as HTMLInputElement).checked,
      slaCfg,
      slaVerifikasiBayarMenit: num('k_slafbayar', k.slaVerifikasiBayarMenit),
      slaVerifikasiRefundMenit: num('k_slafrefund', k.slaVerifikasiRefundMenit),
      slaApprovalMenit: num('k_slaapproval', k.slaApprovalMenit),
      slaPenagihanEskalasiHari: num('k_slapenagih', k.slaPenagihanEskalasiHari),
      ambangBelum: num('k_ambang', 30) || 30,
      simpanMulaiHari: num('k_simpanmulai', k.simpanMulaiHari),
      simpanLepasHari: num('k_simpanlepas', k.simpanLepasHari),
      simpanIntervalHari: num('k_simpaninterval', k.simpanIntervalHari) || 30,
      agingB1Hari: num('k_agb1', k.agingB1Hari), agingB2Hari: num('k_agb2', k.agingB2Hari), agingB3Hari: num('k_agb3', k.agingB3Hari),
      tempoHari: num('k_tempohari', k.tempoHari) || 14,
      dokLinkHari: num('k_doklink', k.dokLinkHari) || 365,
      jedaIngatHari: num('k_jedaingat', k.jedaIngatHari),
      graceEstimasiHari: num('k_grace', k.graceEstimasiHari),
      staleKonfirmasiHari: num('k_stale', k.staleKonfirmasiHari),
      selfieRetensiHari: num('k_selfieret', k.selfieRetensiHari),
      draftKedaluwarsaHari: num('k_draftexp', k.draftKedaluwarsaHari) || 7,
    };
    // Ambang absensi hidup di setelan 'absensi' (dipakai clock-in) — ditulis bersamaan supaya
    // Apo tak perlu pindah tab untuk mengatur hal yang sama-sama soal "berapa lama".
    const nextAb = { ...absensi(), telatSetengah: num('k_telatsetengah', ab.telatSetengah), telatAlpha: num('k_telatalpha', ab.telatAlpha), reminderMenit: num('k_reminderabsen', ab.reminderMenit) };
    const nextSehat: AmbangSehat = {
      kasBulanBahaya: num('s_kasbahaya', sh.kasBulanBahaya), kasBulanPerhatian: num('s_kasperhatian', sh.kasBulanPerhatian),
      macetPersen: num('s_macet', sh.macetPersen), konsentrasiPersen: num('s_konsentrasi', sh.konsentrasiPersen),
      kualitasPersen: num('s_kualitas', sh.kualitasPersen), turunBerturut: num('s_turun', sh.turunBerturut),
    };
    try { await saveSetting('kebijakan', next); await saveSetting('absensi', nextAb); await saveSetting('sehat_ambang', nextSehat); toast('✅ SLA & Waktu disimpan'); } catch (e) { toast((e as Error).message); }
  });
}
